import { useCallback } from 'react';
import {
  ApiResult,
  useAuthenticatedSWR,
  useService,
  wrapErrorWithResult,
} from '../api';
import { SUBSCRIPTIONS_V4_BASE_URL } from './constants';
import { SubscriptionService } from './service';
import { useDeliveryDates } from './useDeliveryDates';
import { useNextDeliveryTarget } from './useNextDeliveryTarget';

export const useSubscription = subscriptionId => {
  const service = useService(SubscriptionService);
  const { data, error, isValidating, mutate } = useAuthenticatedSWR(
    subscriptionId ? `${SUBSCRIPTIONS_V4_BASE_URL}${subscriptionId}/` : null,
  );

  const { updateDeliveryDates } = useDeliveryDates(subscriptionId);
  const { nextDeliveryDate, isNextDeliveryTargetLoading } =
    useNextDeliveryTarget(data);

  // TODO: return response wrapped in ApiResult
  const updateSubscription = useCallback(
    async mealPlanInfo => {
      try {
        const response = await service.updateSubscription(
          subscriptionId,
          mealPlanInfo,
        );
        await updateDeliveryDates();
        mutate(response.data, false);
        return ApiResult.success(response);
      } catch (error) {
        return wrapErrorWithResult(error);
      }
    },
    [mutate, service, subscriptionId, updateDeliveryDates],
  );

  const skipNextDelivery = async () => {
    try {
      const response = await service.skipNextDelivery(subscriptionId);
      mutate();
      return ApiResult.success(response);
    } catch (error) {
      return wrapErrorWithResult(error);
    }
  };

  const updateDeliveryDate = async nextDeliveryTarget => {
    try {
      const response = await service.updateDeliveryDate(
        subscriptionId,
        nextDeliveryTarget,
      );
      mutate();
      return ApiResult.success(response);
    } catch (error) {
      return wrapErrorWithResult(error);
    }
  };

  const reactivatePlan = async (couponCode, campaignId) => {
    try {
      const response = await service.reactivatePlan(
        subscriptionId,
        couponCode,
        campaignId,
      );
      //revalidates the V4 data
      mutate();
      return ApiResult.success(response);
    } catch (error) {
      return wrapErrorWithResult(error);
    }
  };

  const revalidate = useCallback(() => mutate(), [mutate]);

  const isSubscriptionLoading = !data && !error;

  const IsDeliveryTargetLoading = !data?.is_active
    ? false
    : isNextDeliveryTargetLoading;

  return {
    isLoading: subscriptionId
      ? isSubscriptionLoading || IsDeliveryTargetLoading
      : false,
    subscription: data,
    error,
    isValidating,
    refreshSubscription: revalidate,
    updateSubscription,
    skipNextDelivery,
    updateDeliveryDate,
    reactivatePlan,
    nextDeliveryDate,
  };
};
